import * as React from 'preact/compat'
import Api from '../../api'
import Loading from '../Loading'
import Modal from '../Modal'
import buttonsCss from '../../../css/buttons.pcss.json'
import { captureFetchException } from '../../common/helpers'

interface SteamGridItem {
  url: string
}

interface SteamGridGame {
  name: string,
  id: string,
}

export interface SteamGridProps {
  name?: string,
  steamId?: string,
  type: 'grid' | 'hero' | 'grid-vertical',
  onSelect: (url: string, shouldCrop: boolean) => void,
  onClose: () => void
}

interface SteamGridState {
  type: 'grid' | 'hero' | 'grid-vertical',
  loading: boolean,
  error: boolean,
  items?: SteamGridItem[],
  games?: SteamGridGame[],
  selectedGame?: string,
  errorMessage?: string,
}

export default class SteamGrid extends React.Component<SteamGridProps, SteamGridState> {
  state: SteamGridState = {
    type: this.props.type,
    loading: true,
    error: false,
    games: null,
    selectedGame: null,
    errorMessage: null,
  }

  modal = React.createRef<Modal>()

  fetchItems = async () => {
    const { name, steamId, onSelect } = this.props
    const { type, selectedGame } = this.state

    if (selectedGame) {
      return this.getImages(selectedGame)
    }

    const params = new URLSearchParams()
    params.append('id', steamId || name)
    params.append('type', type)
    Api.get<Array<SteamGridItem> | {
      games: Array<SteamGridGame>,
    } | {
      error: string
    }>({ url: `steam-grid/items?${params.toString()}` })
      .then(response => {
        if (!Array.isArray(response)) {
          if ('error' in response) {
            this.setState({
              loading: false,
              error: true,
              errorMessage: response.error
            })
            return
          }

          if ('games' in response) {
            this.setState({
              loading: false,
              games: response.games
            })
            return
          }

          throw new Error('Invalid response')
        } else {
          if (response.length === 1) {
            onSelect(response[0].url, type === 'grid-vertical')
            this.close()
          }
          this.setState({
            loading: false,
            items: response,
          })
        }
      })
      .catch(error => {
        captureFetchException(error)
        this.setState({
          loading: false,
          error: true,
        })
      })
  }

  getImages = async (id: string) => {
    const { onSelect } = this.props
    const { type } = this.state
    const params = new URLSearchParams()
    params.append('id', id)
    params.append('type', type)
    this.setState({
      loading: true,
      games: null,
      selectedGame: id,
      errorMessage: null,
    })
    Api.get<Array<SteamGridItem> | {
      error: string
    }>({ url: `steam-grid/items-by-id?${params.toString()}` })
      .then(response => {
        if (!Array.isArray(response)) {
          this.setState({
            loading: false,
            error: true,
            errorMessage: response.error
          })
          return
        }

        if (response.length === 1) {
          onSelect(response[0].url, type === 'grid-vertical')
          this.close()
        }
        this.setState({
          loading: false,
          items: response,
        })
      })
      .catch(error => {
        captureFetchException(error)
        this.setState({
          loading: false,
          error: true,
        })
      })
  }

  componentDidMount() {
    this.fetchItems()
      .catch(error => {
        captureFetchException(error)
        this.setState({
          loading: false,
          error: true,
        })
      })
  }

  searchVertical = () => {
    this.setState({
      type: 'grid-vertical',
      loading: true,
      items: null,
      error: false,
      errorMessage: null,
    }, () => {
      this.fetchItems()
        .catch(error => {
          captureFetchException(error)
          this.setState({
            loading: false,
            error: true,
          })
        })
    })
  }

  close = () => {
    // noinspection JSIgnoredPromiseFromCall
    this.modal.current?.close()
  }

  render() {
    const { onClose, onSelect } = this.props
    const { type, loading, games, items, error, errorMessage } = this.state
    let width = '250px';
    let maxWidth = 'auto';
    let height = '250px';
    let containerMaxWidth = 'auto';
    let aspectRatio = '1';
    let searchVertical = null;

    switch (type) {
      case 'grid':
        searchVertical = <button
          type='button'
          className={buttonsCss['primary']}
          style='margin-top: 16px'
          onClick={this.searchVertical}
        >
          Искать вертикальные
        </button>
        break;
      case 'hero':
        width = '100%';
        maxWidth = '1280px';
        height = 'auto';
        containerMaxWidth = '1280px';
        aspectRatio = '96/31';
        break;
      case 'grid-vertical':
        aspectRatio = '2/3'
        width = '166px'
        break;
    }

    return (
      <Modal ref={this.modal} onClose={onClose} showClose disableOutsideClose>
        {loading && <Loading/>}
        {error && <div style='display: flex; flex-direction: column; align-items: center'>
          <p>{errorMessage || 'При загрузке обложек произошла ошибка…'}</p>
          <button type='button' className={buttonsCss['primary']} style='margin-top: 16px' onClick={this.close}>
            Эх…
          </button>
        </div>}
        {games && <>
          <p>Выбери игру из списка</p>
          <div style='display: flex; flex-direction: column; row-gap: 16px;'>
            {games.map(game => <button
              type="button"
              className={buttonsCss['default']}
              onClick={() => this.getImages(game.id)}
            >{game.name}</button>)}
          </div>
        </>}
        {items && items.length === 0 && <div style='display: flex; flex-direction: column; align-items: center'>
          <p>Нет обложек для этой игры</p>
          {searchVertical}
        </div>}
        {items && items.length > 0 && <>
          <p>Выбери обложку для игры:</p>
          <div style={{
            display: 'flex',
            flexWrap: 'wrap',
            columnGap: '16px',
            rowGap: '16px',
            maxWidth: containerMaxWidth
          }}>
            {items.map(item => <img
              alt=''
              src={item.url}
              style={{
                width: width,
                height: height,
                maxWidth: maxWidth,
                borderRadius: '8px',
                cursor: 'pointer',
                aspectRatio: aspectRatio,
                objectFit: 'contain'
              }}
              onClick={() => {
                onSelect(item.url, type === 'grid-vertical')
                this.close()
              }}
            />)}
          </div>
          {searchVertical}
        </>}
      </Modal>
    )
  }
}
